import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/shared/ui/card';
import { Loader2, Moon, Sun } from 'lucide-react';
import { getUserProfile, updateUserProfile } from '../api/profile-api';
import { handleApiError } from '@/shared/api/requests';

export const DisplaySettings: React.FC = () => {
  // Initialise from the DOM immediately so the correct button is highlighted
  // the instant the tab renders, before the fetch completes.
  const [prefersDark, setPrefersDark] = useState<boolean>(
    () => document.documentElement.classList.contains('dark')
  );
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Silently sync with the server value in the background.
  useEffect(() => {
    getUserProfile()
      .then((profile) => setPrefersDark(profile.prefers_dark))
      .catch((err) => setError(handleApiError(err)));
  }, []);

  const handleSelect = async (dark: boolean) => {
    if (saving || prefersDark === dark) return;
    setSaving(true);
    setError(null);
    try {
      await updateUserProfile({ prefers_dark: dark });
      setPrefersDark(dark);
      if (dark) {
        document.documentElement.classList.add('dark');
      } else {
        document.documentElement.classList.remove('dark');
      }
    } catch (err) {
      setError(handleApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Display</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          <p className="text-sm text-muted-foreground">
            Choose your preferred display mode. This setting is saved to your account.
          </p>

          {error && (
            <p className="text-sm text-destructive">{error}</p>
          )}

          <div className="flex gap-4 pt-2">
            {/* Light mode option */}
            <button
              type="button"
              disabled={saving}
              onClick={() => handleSelect(false)}
              className={`relative flex-1 flex flex-col items-center gap-3 p-6 rounded-xl border-2 transition-all duration-200 ${
                !prefersDark
                  ? 'border-primary bg-primary/5 shadow-sm'
                  : 'border-border bg-card hover:border-border/80 hover:bg-accent/50'
              } ${saving ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer'}`}
            >
              <div className={`p-4 rounded-full ${!prefersDark ? 'bg-amber-100 dark:bg-amber-950/50' : 'bg-muted'}`}>
                <Sun className={`w-8 h-8 ${!prefersDark ? 'text-amber-500' : 'text-muted-foreground'}`} />
              </div>
              <div className="text-center">
                <p className={`font-semibold text-sm ${!prefersDark ? 'text-foreground' : 'text-muted-foreground'}`}>
                  Light
                </p>
                <p className="text-xs text-muted-foreground mt-0.5">Clean white interface</p>
              </div>
              {!prefersDark && (
                <div className="absolute top-3 right-3 w-2.5 h-2.5 rounded-full bg-primary" />
              )}
            </button>

            {/* Dark mode option */}
            <button
              type="button"
              disabled={saving}
              onClick={() => handleSelect(true)}
              className={`relative flex-1 flex flex-col items-center gap-3 p-6 rounded-xl border-2 transition-all duration-200 ${
                prefersDark
                  ? 'border-primary bg-primary/5 shadow-sm'
                  : 'border-border bg-card hover:border-border/80 hover:bg-accent/50'
              } ${saving ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer'}`}
            >
              <div className={`p-4 rounded-full ${prefersDark ? 'bg-indigo-100 dark:bg-indigo-950/50' : 'bg-muted'}`}>
                <Moon className={`w-8 h-8 ${prefersDark ? 'text-indigo-500 dark:text-indigo-400' : 'text-muted-foreground'}`} />
              </div>
              <div className="text-center">
                <p className={`font-semibold text-sm ${prefersDark ? 'text-foreground' : 'text-muted-foreground'}`}>
                  Dark
                </p>
                <p className="text-xs text-muted-foreground mt-0.5">Easy on the eyes</p>
              </div>
              {prefersDark && (
                <div className="absolute top-3 right-3 w-2.5 h-2.5 rounded-full bg-primary" />
              )}
            </button>
          </div>

          {saving && (
            <p className="text-xs text-muted-foreground flex items-center gap-1.5 pt-1">
              <Loader2 className="w-3 h-3 animate-spin" />
              Saving preference...
            </p>
          )}
        </div>
      </CardContent>
    </Card>
  );
};
